import React, { FC, PointerEvent, useCallback, useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';

// IL BORDO DI UN PANNELLO SI DEVE VEDERE (round 4, prototipo V2). Il bordo dei blocchi di pagina,
// `#eef1f3`, sul fondo grigio del workspace quasi non c'era: pannello e palco si toccavano senza una
// cucitura. È la riga dei divisori del Backoffice, la stessa che il prototipo dà a ogni pannello.
export const Panel = styled.div`
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    background: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.backgroundColor};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.borderRadius};
    box-shadow: 0 1px 2px rgba(38, 50, 56, 0.06);
`;

export const Workspace = styled.div`
    position: relative;
    display: grid;
    grid-template-columns: var(--col-left) minmax(0, 1fr) var(--col-right);
    gap: var(--gap-panel);
    padding: 0 var(--gap-panel) var(--gap-panel);
    min-width: 0;
    min-height: 0;
    height: 100%;

    --col-left: var(--w-left);
    --col-right: var(--w-right);

    /* LO SPAZIO FRA LE COLONNE HA UN NOME, perché non lo usa solo la griglia: ci passa in
       mezzo la presa del divisore, che deve stare sulla cucitura. Scritto due volte, il
       giorno che cambia si muove solo una delle due cose. */
    --gap-panel: 6px;

    &[data-layout='drawer'] {
        grid-template-columns: var(--col-left) minmax(0, 1fr);
    }

    &[data-layout='workspace'][data-dock='side'] {
        grid-template-columns: var(--col-left) var(--col-right) minmax(0, 1fr);

        > [data-panel='left'] {
            grid-column: 1;
            grid-row: 1;
        }

        > [data-panel='right'] {
            grid-column: 2;
            grid-row: 1;
        }

        > [data-panel='stage'] {
            grid-column: 3;
            grid-row: 1;
        }
    }

    /* UNA TRACCIA ESISTE ANCHE SENZA NIENTE DENTRO. Togliere il pannello dell'Inspector dal
       DOM non toglie la sua colonna: la griglia ne dichiara tre e tre ne tiene, quindi al
       posto del pannello restava una banda vuota larga quanto lui, più il suo spazio di
       mezzo. Le tracce diventano due — e non una larga zero, che lascerebbe comunque il
       doppio gap fra le altre. Chi non ha soggetto non ha colonna, e non ha nemmeno traccia.

       Le due tracce si riscrivono UNA VOLTA PER ATTRACCO: la regola di side ne dichiara tre
       con due attributi, e una riga che ne dichiarasse due con un attributo solo perderebbe
       il confronto — il palco finirebbe schiacciato nella traccia stretta. */
    &[data-inspector='off'] {
        grid-template-columns: var(--col-left) minmax(0, 1fr);
    }

    &[data-layout='workspace'][data-dock='side'][data-inspector='off'] {
        grid-template-columns: var(--col-left) minmax(0, 1fr);

        > [data-panel='stage'] {
            grid-column: 2;
        }
    }

    &[data-collapse~='left'] {
        --col-left: var(--w-rail-cur);
    }

    &[data-collapse~='right'] {
        --col-right: var(--w-rail-cur);
    }

    @media (max-width: 940px) {
        grid-template-columns: minmax(0, 1fr);

        > [data-panel='left'],
        > [data-panel='right'] {
            display: none;
        }

        &[data-collapse] {
            grid-template-columns: minmax(0, 1fr);
        }
    }
`;

export const StageColumn = styled.div`
    position: relative;
    display: grid;
    grid-template-rows: minmax(0, 1fr);
    min-width: 0;
    min-height: 0;

    @media (max-width: 940px) {
        grid-column: 1;
    }
`;

// MENTRE SI TRASCINA, LA PAGINA INTERA È IN TRASCINAMENTO. `document.body` prendeva già la
// classe `resizing`, ma **nessuna regola la leggeva**: il puntatore usciva dal manico e il
// cursore tornava quello normale, e soprattutto i movimenti finivano sulla tela del 3D, che
// faceva ruotare il modello mentre stavi allargando un pannello.
//
// Il cursore resta quello della presa, e per la durata del gesto nessun
// altro elemento riceve il puntatore — il manico sì, altrimenti perderebbe il proprio.
const ResizingStyles = createGlobalStyle`
    body.resizing {
        cursor: col-resize;
    }

    body.resizing canvas,
    body.resizing iframe {
        pointer-events: none;
    }
`;

const ResizerButton = styled.button<{ $direction: ResizerDirection; $dragging?: boolean }>`
    position: absolute;
    z-index: 5;
    border: 0;
    padding: 0;
    background: transparent;
    color: ${(props) => props.theme.app.borderColor};
    cursor: col-resize;
    touch-action: none;

    &::before {
        content: '';
        position: absolute;
        background: currentColor;
        opacity: 0;
        transition: opacity 120ms ease;
    }

    /* LA LINGUETTA DI PRESA. La linea da un pixel dice che lì c'è un confine; non dice che si
       può tirare. Il cursore lo direbbe, ma solo a chi ci è già sopra col puntatore — e per
       arrivarci bisogna prima sospettare che ci sia qualcosa. La linguetta compare al
       passaggio, insieme alla linea, ed è larga abbastanza da leggersi come una maniglia. */
    &::after {
        content: '';
        position: absolute;
        border-radius: 999px;
        background: ${(props) => props.theme.app.secondary};
        opacity: 0;
        transition: opacity 130ms ease;
        pointer-events: none;
    }

    &:hover,
    &:focus-visible {
        color: ${(props) => props.theme.app.secondary};
        outline: none;
    }

    &:hover::before,
    &:focus-visible::before,
    &:hover::after,
    &:focus-visible::after {
        opacity: 1;
    }

    /* Mentre si tira, il puntatore è dove capita: la presa resta accesa perché è lei che si sta
       muovendo. */
    ${(props) =>
        props.$dragging &&
        css`
            color: ${props.theme.app.secondary};

            &::before,
            &::after {
                opacity: 1;
            }
        `}

    /* LA PRESA DI SINISTRA NON STA DENTRO IL PANNELLO, ed è il motivo per cui non si poteva
       afferrare: il pannello nasconde ciò che esce dal suo bordo — gli serve, per il proprio
       raggio — e la presa sporge. Per di più il pannello non è posizionato, quindi la presa non
       si misurava su di lui ma sul workspace, cioè dall'altra parte dello schermo.
       Ora è figlia del workspace e si posiziona da sé sulla cucitura: dove finisce la prima
       colonna, meno mezza larghezza della presa. */
    ${(props) =>
        props.$direction === 'left' &&
        css`
            top: 0;
            left: calc(var(--gap-panel) + var(--col-left) - 4.5px);
            bottom: 0;
            width: 9px;

            &::before {
                top: 50%;
                left: 4px;
                width: 1px;
                height: 34px;
                transform: translateY(-50%);
            }

            &::after {
                top: 50%;
                left: 2px;
                width: 5px;
                height: 34px;
                transform: translateY(-50%);
            }
        `}

    ${(props) =>
        props.$direction === 'right' &&
        css`
            top: 0;
            right: -4px;
            bottom: 0;
            width: 9px;

            &::before {
                top: 50%;
                left: 4px;
                width: 1px;
                height: 34px;
                transform: translateY(-50%);
            }

            &::after {
                top: 50%;
                left: 2px;
                width: 5px;
                height: 34px;
                transform: translateY(-50%);
            }
        `}

    /* LA CUCITURA FRA IL SECONDO PANNELLO E IL PALCO. Nel secondo attracco l'Inspector non è
       l'ultima colonna ma la seconda, e il suo confine cade dove finisce: dopo il rientro, la
       prima colonna, lo spazio di mezzo e la seconda. Le stesse quattro misure che la griglia
       usa per disegnarla — scritte qui in aritmetica, come già fa la presa di sinistra. */
    ${(props) =>
        props.$direction === 'side' &&
        css`
            top: 0;
            left: calc(var(--gap-panel) + var(--col-left) + var(--gap-panel) + var(--col-right) - 4.5px);
            bottom: 0;
            width: 9px;

            &::before {
                top: 50%;
                left: 4px;
                width: 1px;
                height: 34px;
                transform: translateY(-50%);
            }

            &::after {
                top: 50%;
                left: 2px;
                width: 5px;
                height: 34px;
                transform: translateY(-50%);
            }
        `}
`;

/** Lo stesso numero di `--gap-panel`: la griglia lo usa in CSS, il trascinamento in aritmetica. */
const WORKSPACE_GAP = 6;

// I VERSI SONO TRE PERCHÉ SONO TRE LE CUCITURE, non perché servano tre comandi: da che parte
// cresce la colonna quando tiri, e rispetto a quale bordo si misura il puntatore.
//     left    la prima colonna, dal bordo sinistro del workspace
//     right   l'ultima colonna, dal bordo destro
//     side    una colonna IN MEZZO — cresce a destra, e si misura dal proprio bordo sinistro
export type ResizerDirection = 'left' | 'right' | 'side';

interface ResizerProps {
    direction: ResizerDirection;
    value: number;
    min: number;
    max: number;
    defaultValue: number;
    workspaceRef: React.RefObject<HTMLDivElement | null>;
    referenceRef?: React.RefObject<HTMLDivElement | null>;
    onCommit: (value: number) => void;
    cssVariable: string;
}

export const Resizer: FC<ResizerProps> = ({ direction, value, min, max, defaultValue, workspaceRef, referenceRef, onCommit, cssVariable }) => {
    const valueRef = useRef(value);
    valueRef.current = value;
    const [dragging, setDragging] = useState(false);

    const clamp = useCallback((next: number) => Math.min(Math.max(next, min), max), [max, min]);

    const applyValue = useCallback(
        (next: number) => {
            const resolved = clamp(next);
            valueRef.current = resolved;
            workspaceRef.current?.style.setProperty(cssVariable, `${resolved}px`);
            return resolved;
        },
        [clamp, cssVariable, workspaceRef]
    );

    const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
        event.preventDefault();
        const workspace = workspaceRef.current;
        if (!workspace) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
        document.body.classList.add('resizing');
        document.body.style.userSelect = 'none';
        // IL RIFERIMENTO È UN BORDO CHE NON SI MUOVE MENTRE TIRI. Per l'ultima colonna è il bordo
        // destro del palco; per una colonna in mezzo è il proprio bordo SINISTRO, che resta dov'è
        // mentre la colonna si allarga. Misurarla dal palco non funzionerebbe: il palco si
        // stringe man mano, e il bordo preso a riferimento scapperebbe sotto il puntatore.
        const reference = direction === 'right' || direction === 'side' ? (referenceRef?.current ?? workspace) : workspace;
        const rect = reference.getBoundingClientRect();

        const handlePointerMove = (moveEvent: globalThis.PointerEvent) => {
            // La colonna comincia dopo il margine del workspace: senza toglierlo, il pannello
            // resterebbe largo sei pixel più di dove si trova il puntatore.
            const next =
                direction === 'left'
                    ? moveEvent.clientX - rect.left - WORKSPACE_GAP
                    : direction === 'side'
                      ? moveEvent.clientX - rect.left
                      : rect.right - moveEvent.clientX;
            applyValue(next);
        };
        const handlePointerUp = () => {
            setDragging(false);
            document.body.classList.remove('resizing');
            document.body.style.userSelect = '';
            onCommit(valueRef.current);
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerUp);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp, { once: true });
        window.addEventListener('pointercancel', handlePointerUp, { once: true });
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        const step = event.shiftKey ? 32 : 8;
        let delta = 0;

        if (direction === 'left' || direction === 'side') {
            if (event.key === 'ArrowRight') delta = step;
            if (event.key === 'ArrowLeft') delta = -step;
        } else {
            if (event.key === 'ArrowLeft') delta = step;
            if (event.key === 'ArrowRight') delta = -step;
        }

        if (delta === 0) return;
        event.preventDefault();
        onCommit(applyValue(valueRef.current + delta));
    };

    useEffect(
        () => () => {
            document.body.classList.remove('resizing');
            document.body.style.userSelect = '';
        },
        []
    );

    return (
        <>
            <ResizingStyles />
            <ResizerButton
                type='button'
                $direction={direction}
                $dragging={dragging}
                role='separator'
                aria-orientation='vertical'
                aria-valuenow={value}
                aria-valuemin={min}
                aria-valuemax={max}
                aria-label={`Resize ${direction} panel`}
                onPointerDown={handlePointerDown}
                onKeyDown={handleKeyDown}
                onDoubleClick={() => onCommit(applyValue(defaultValue))}
            />
        </>
    );
};
